import React, { useState, useRef } from 'react';
import Image from 'next/image';
import { X, Search } from 'lucide-react';
import { useProducts } from '@/data/product';
import { Input } from '@nextui-org/react';
import { useDebounce } from '@/lib/hooks/use-debounce';
import { SortOrder } from '@/types';

interface LoraModel {
  id: string;
  name: string;
  image: string;
  trigger_word: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (model: LoraModel | null) => void;
}

const ModelCard = ({ model, onSelect, onClose }: { 
  model: any; 
  onSelect: (model: LoraModel) => void;
  onClose: () => void;
}) => (
  <div
    onClick={() => {
      onSelect({
        id: model.id,
        name: model.name,
        image: model.image?.thumbnail || model.image?.original || '/placeholder.svg',
        trigger_word: model.trigger_word || ''
      });
      onClose();
    }}
    className="group relative cursor-pointer overflow-hidden rounded-xl bg-gray-50 transition-all hover:shadow-lg dark:bg-gray-800/50"
  >
    <div className="aspect-[4/3] w-full overflow-hidden">
      <Image
        src={model.image?.thumbnail || model.image?.original || '/placeholder.svg'}
        alt={model.name}
        width={400}
        height={300}
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    </div>
    
    <div className="absolute bottom-0 left-0 right-0 p-4 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
      <h3 className="text-sm font-medium line-clamp-1">{model.name}</h3>
      <p className="mt-1 text-xs text-gray-200 line-clamp-1">
        Trigger: {model.trigger_word || 'N/A'}
      </p>
    </div>

    <div className="p-4">
      <h3 className="font-medium line-clamp-1 group-hover:text-brand">{model.name}</h3>
      <p className="mt-1 text-xs text-gray-500 line-clamp-1 dark:text-gray-400">
        Trigger: {model.trigger_word || 'N/A'}
      </p>
    </div>
  </div>
);

const LoraModelSelector = ({ isOpen, onClose, onSelect }: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const searchRef = useRef<HTMLInputElement>(null);

  // Featured models query
  const { products: featuredProducts } = useProducts(
    {
      is_custom_ai_model: true,
      limit: 3,
      orderBy: 'orders_count',
      sortedBy: 'desc' as SortOrder,
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  // Searched models query
  const { products: searchedProducts } = useProducts(
    {
      is_custom_ai_model: true,
      limit: 6,
      name: debouncedSearch,
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(debouncedSearch),
    }
  );

  // Merge and deduplicate products
  const displayedModels = React.useMemo(() => {
    if (!debouncedSearch) {
      return featuredProducts;
    }
    
    const seen = new Set();
    return [...featuredProducts, ...searchedProducts].filter(product => {
      if (seen.has(product.id)) {
        return false;
      }
      seen.add(product.id);
      return true;
    });
  }, [featuredProducts, searchedProducts, debouncedSearch]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="relative h-[80vh] w-full max-w-3xl rounded-xl bg-white p-6 dark:bg-gray-800">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="mb-4 text-xl font-semibold">Select LoRA Model</h2>
        
        <div className="relative mb-6">
          <Input
            ref={searchRef}
            type="text"
            placeholder="Search models..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startContent={<Search className="h-4 w-4 text-gray-400" />}
            classNames={{
              input: "pl-3",
              inputWrapper: "bg-gray-50 dark:bg-gray-700",
            }}
          />
        </div>

        <div className="h-[calc(80vh-180px)] max-h-[600px] overflow-y-auto px-1">
          {!debouncedSearch && (
            <div className="sticky top-0 z-10 mb-4 bg-white pb-2 pt-1 dark:bg-gray-800">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Featured Models
              </h3>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {displayedModels.map((model) => (
              <ModelCard 
                key={model.id}
                model={model}
                onSelect={onSelect}
                onClose={onClose}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoraModelSelector; 